import { ModelError, ModelErrorCode } from '../errors/ModelError.js';
import { Option, OptionGroup, OptionGroupId, OptionId } from '../option.js';
import { TypeReference } from '../types.js';


export type OptionCreator = () => Option;

export class OptionsRegistry {
  private options = new Map<OptionId, Option>();
  private lazyOptions = new Map<OptionId, OptionCreator>();
  private optionIdsByType = new Map<TypeReference, OptionId[]>();
  private optionGroups = new Map<OptionGroupId, OptionGroup>();

  constructor() {}

  clone() {
    const self = new OptionsRegistry();
    self.options = new Map<OptionId, Option>(this.options);
    self.lazyOptions = new Map<OptionId, OptionCreator>(this.lazyOptions);
    self.optionIdsByType = new Map<TypeReference, OptionId[]>(this.optionIdsByType);
    self.optionGroups = new Map<OptionGroupId, OptionGroup>(this.optionGroups);
    return self;
  }
  registerOption(id: OptionId, type: TypeReference, optionCreator: () => Option): void {
    this.lazyOptions.set(id, optionCreator);
    this.registerOptionIdWithType(id, type);
  }

  addOption(id: OptionId, option: Option): void {
    this.options.set(id, option);
    this.registerOptionIdWithType(id, option.type.typeRef);
  }

  addOptionGroup(id: OptionGroupId, optionGroup: OptionGroup): void {
    this.optionGroups.set(id, optionGroup);
  }

  getOptions(): Option[] {
    if (this.lazyOptions.size > 0) {
      console.warn('This operation will materialize all lazy options!');
    }
    for (const id of this.lazyOptions.keys()) {
      this.materializeOptionWithId(id);
    }
    return Array.from(this.options.values());
  }

  getOptionGroups(): OptionGroup[] {
    return Array.from(this.optionGroups.values());
  }

  hasOption(id: OptionId): boolean {
    return this.options.has(id) || this.lazyOptions.has(id);
  }

  hasOptionGroup(id: OptionGroupId): boolean {
    return this.optionGroups.has(id);
  }

  getOptionById(id: OptionId): Option {
    if (typeof id === 'undefined') {
      return undefined;
    }
    // support null value in SourcedValue
    if (id === null) {
      return null;
    }
    if (typeof id === 'string') {
      if (this.hasOption(id)) {
        const option = this.options.get(id);
        if (!option) {
          this.materializeOptionWithId(id);
        }
        return this.options.get(id);
      }
    }

    throw new ModelError(ModelErrorCode.CANNOT_FIND_OPTION, `Cannot find option ${JSON.stringify(id)}`);
  }

  findOptionsIdsWithType(typeRef: TypeReference): OptionId[] {
    return this.optionIdsByType.get(typeRef) ?? [];
  }

  findOptionsWithType(typeRef: TypeReference): Option[] {
    return this.getOptions().filter(v => v.type.typeRef === typeRef) as Option[];
  }

  getOptionOrOptionGroupById(id: OptionId | OptionGroupId): Option | OptionGroup {
    if (typeof id === 'undefined') {
      return undefined;
    }
    // support null value in SourcedValue
    if (id === null) {
      return null;
    }
    if (typeof id === 'string') {
      if (this.hasOption(id)) {
        const option = this.options.get(id);
        if (!option) {
          this.materializeOptionWithId(id);
        }
        return this.options.get(id);
      }
      if (this.hasOptionGroup(id)) {
        return this.optionGroups.get(id);
      }
    }

    throw new ModelError(ModelErrorCode.CANNOT_FIND_OPTION, `Cannot find option or option group ${JSON.stringify(id)}`);
  }

  findOptionGroupById(id: OptionGroupId): OptionGroup | undefined {
    return this.optionGroups.get(id);
  }

  private materializeOptionWithId(id: OptionId) {
    const optionCreator = this.lazyOptions.get(id);
    if (optionCreator) {
      this.lazyOptions.delete(id);
      this.options.set(id, optionCreator());
    }
  }

  private registerOptionIdWithType(id: OptionId, type: TypeReference) {
    const ids = this.optionIdsByType.get(type);
    if (ids) {
      ids.push(id);
    } else {
      this.optionIdsByType.set(type, [id]);
    }
  }

  public get getLazyOptions(): Readonly<Map<string, OptionCreator>> {
    return this.lazyOptions;
  }
}
